<script setup lang="ts">
import type { Link } from 'types';
import type { SearchSuggestion } from 'types/search';

const props = defineProps<{
  searchVisible?: boolean;
  searchPage: Link;
}>();

const inputValue = shallowRef<string>('');
const inputWrapper = useTemplateRef('inputWrapper');
const input = useTemplateRef('input');
const suggestions = ref<SearchSuggestion[]>([]);
const activeSuggestionIndex = ref<number>(-1);
const isInteractingWithSuggestions = ref(false);

const { query } = useRoute();
const search = useSearchApi();
const dom = useDom();
const { focused } = useFocusWithin(inputWrapper);

function clearSuggestions() {
  suggestions.value = [];
  activeSuggestionIndex.value = -1;
}

function blurSearchInput() {
  clearSuggestions();
  input.value?.blur();
}

async function submitSearch(suggestion?: SearchSuggestion) {
  if (suggestion?.pageUrl) {
    await navigateTo({
      path: handleRelativeUrl(suggestion.pageUrl)
    });
  } else if (inputValue.value) {
    await navigateTo({
      path: handleRelativeUrl(props.searchPage.url),
      query: { term: inputValue.value }
    });
  }

  blurSearchInput();
}

const getSuggestionsDebounced = useDebounceFn(getSuggestions, 150);

async function getSuggestions() {
  if (inputValue.value.length < 2) {
    clearSuggestions();
    return;
  }

  const result = await search.getSearchSuggestions(inputValue.value);
  suggestions.value = result.searchSuggestions;
}

onMounted(() => {
  if (query.term) {
    inputValue.value = String(query.term);
  }

  nextTick(() => {
    input.value?.focus();
  });
});

const { down, up } = useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (focused.value && (down?.value || up?.value)) {
      e.preventDefault();
    }
  }
});

watch(focused, (isFocused) => {
  if (!isFocused && !isInteractingWithSuggestions.value) {
    activeSuggestionIndex.value = -1;
  }
});

watchEffect(() => {
  if (!focused.value) return;

  if (down?.value && activeSuggestionIndex.value < suggestions.value.length - 1) {
    activeSuggestionIndex.value += 1;
  }

  if (up?.value && activeSuggestionIndex.value > -1) {
    activeSuggestionIndex.value -= 1;
  }

  if (activeSuggestionIndex.value >= 0) {
    nextTick(() => {
      dom?.document.getElementById(`suggestion-${activeSuggestionIndex.value}`)?.focus();
    });
  } else if (activeSuggestionIndex.value === -1 && (up?.value || down?.value)) {
    nextTick(() => {
      input.value?.focus();
    });
  }
});

function handleSuggestionPointerDown() {
  isInteractingWithSuggestions.value = true;
}

function handleSuggestionClick(suggestion: SearchSuggestion) {
  submitSearch(suggestion);
  isInteractingWithSuggestions.value = false;
}
</script>

<template>
  <div class="z-40 w-full bg-white py-4 md:py-8">
    <div class="container">
      <div ref="inputWrapper">
        <form
          class="relative"
          @submit.prevent="submitSearch()"
        >
          <input
            ref="input"
            v-model="inputValue"
            type="text"
            name="search"
            :placeholder="$t('General.Search.Placeholder')"
            class="w-full border border-gray-60 p-5 text-lg text-main"
            required
            @input="getSuggestionsDebounced"
          />

          <div class="absolute right-0 top-0 flex aspect-square h-full p-2">
            <button
              type="submit"
              tabindex="-1"
              :class="[
                'flex size-full cursor-pointer items-center justify-center bg-primary',
                'transition-color duration-300 hover:bg-black'
              ]"
            >
              <BaseIcon
                name="search"
                fill-color="var(--color-white)"
              />
            </button>
          </div>
        </form>

        <ul
          v-if="suggestions.length && (focused || isInteractingWithSuggestions)"
          :aria-label="$t('Aria.Global.Header.SearchSuggestionList')"
          class="border-x border-b border-gray-60"
        >
          <li
            v-for="(suggestion, index) in suggestions"
            :id="`suggestion-${index}`"
            :key="`${suggestion.pageName}-${index}`"
            tabindex="0"
            role="option"
            :aria-selected="activeSuggestionIndex === index"
            class="cursor-pointer px-4 py-3 outline-none odd:bg-gray-20 hover:bg-primary hover:text-white focus:bg-primary focus:text-white"
            @pointerdown.prevent="handleSuggestionPointerDown"
            @click="handleSuggestionClick(suggestion)"
            @keydown.enter="submitSearch(suggestion)"
            @keydown.space.prevent="submitSearch(suggestion)"
          >
            <p>{{ suggestion.pageName }}</p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
